/** DataBest — Caregiver "Today" landing page. Mobile-first. */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    PlayCircle, CheckCircle2, Clock, AlertCircle, Loader2, ChevronLeft,
    Pencil, AlertOctagon, ChevronRight,
} from "lucide-react";
import { scoringApi } from "@/lib/scoringApi";
import { backlogApi } from "@/lib/backlogApi";
import { individualsApi } from "@/lib/planApi";
import useAlertsStore from "@/store/alertsStore";
import ScoringForm from "@/features/scoring/components/ScoringForm";
import FlowHeader from "@/features/scoring/components/FlowHeader";
import VoiceReportButton from "@/features/voice/components/VoiceReportButton";
import VoiceResultPanel from "@/features/voice/components/VoiceResultPanel";

const TodayPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tasks, setTasks] = useState([]);
    const [individuals, setIndividuals] = useState({});
    const [selectedIndividual, setSelectedIndividual] = useState(null);
    const [loading, setLoading] = useState(true);
    // When arriving via /today?task_id=X we need to block the landing render
    // until the backlog fetch resolves — otherwise the caregiver sees a brief
    // flash of the landing page before the flow view swaps in.
    // Initialised synchronously from the URL so the FIRST render already skips landing.
    const [deepLinkPending, setDeepLinkPending] = useState(() =>
        Boolean(searchParams.get("task_id")),
    );
    // Flow state — snapshot captured on entry so Save doesn't collapse the flow.
    const [flowIndex, setFlowIndex] = useState(-1);
    const [flowMode, setFlowMode] = useState("pending"); // 'pending' | 'review' | 'backlog'
    const [flowTasks, setFlowTasks] = useState([]);
    const backlogTotal = useAlertsStore((s) => s.summary.total);
    const refreshAlerts = useAlertsStore((s) => s.refresh);
    const [voiceResult, setVoiceResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [t, list] = await Promise.all([
                scoringApi.todayTasks(),
                individualsApi.list({ active: true }),
            ]);
            setTasks(t.tasks || []);
            const map = {};
            (list || []).forEach((i) => (map[i.id] = i));
            setIndividuals(map);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Deep-link support: /today?task_id=X → snapshot the FULL backlog list
    // (ordered by urgency by the backend), position the flow at the clicked task,
    // and augment each item with `.backlog` metadata so the ScoringForm can render
    // the urgency banner (level, days overdue, expected due date).
    useEffect(() => {
        const taskId = searchParams.get("task_id");
        if (!taskId) {
            // No deep-link on this render — make sure we don't block landing.
            setDeepLinkPending(false);
            return;
        }

        let cancelled = false;
        // Hard timeout so a stalled backlog fetch can never leave the user
        // staring at an infinite spinner. 15 seconds is generous.
        const timer = setTimeout(() => {
            if (cancelled) return;
            cancelled = true;
            setDeepLinkPending(false);
            setSearchParams({}, { replace: true });
            toast.error("Loading took too long. Please try again from Backlog.");
        }, 15000);

        (async () => {
            try {
                const r = await backlogApi.list({});
                if (cancelled) return;
                const items = r?.items || [];

                const mapped = items.map((b) => ({
                    task: b.task,
                    individual_id: b.individual.id,
                    outcome: {
                        title: b.outcome.title,
                        life_area: b.outcome.life_area,
                    },
                    measured_today: false, // never in backlog if measured today
                    backlog: {
                        level: b.level,
                        days_overdue: b.days_overdue,
                        last_measured_at: b.last_measured_at,
                        interval_days: b.interval_days,
                    },
                }));

                const foundIdx = mapped.findIndex(
                    (m) => m.task.id === taskId,
                );
                if (foundIdx < 0) {
                    toast.warning(
                        "That task is no longer overdue — someone may have already scored it.",
                    );
                    setDeepLinkPending(false);
                    setSearchParams({}, { replace: true });
                    return;
                }

                setFlowTasks(mapped);
                setFlowMode("backlog");
                setFlowIndex(foundIdx);
                setSearchParams({}, { replace: true });
                setDeepLinkPending(false);
            } catch (_) {
                if (cancelled) return;
                toast.error("Could not load the backlog. Please try again.");
                setDeepLinkPending(false);
                setSearchParams({}, { replace: true });
            }
        })();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchParams, setSearchParams]);

    const filtered = useMemo(() => {
        if (!selectedIndividual) return tasks;
        return tasks.filter((t) => t.individual_id === selectedIndividual);
    }, [tasks, selectedIndividual]);

    const stats = useMemo(() => {
        const total = filtered.length;
        const done = filtered.filter((t) => t.measured_today).length;
        return { total, done, pending: total - done };
    }, [filtered]);

    const inFlow = flowIndex >= 0 && flowIndex < flowTasks.length;

    const startPendingFlow = () => {
        const snapshot = filtered.filter((t) => !t.measured_today);
        if (snapshot.length === 0) return;
        setFlowTasks(snapshot);
        setFlowMode("pending");
        setFlowIndex(0);
    };

    const startReviewFlow = () => {
        const snapshot = filtered.filter((t) => t.measured_today);
        if (snapshot.length === 0) return;
        setFlowTasks(snapshot);
        setFlowMode("review");
        setFlowIndex(0);
    };

    const goBackToToday = async () => {
        setFlowIndex(-1);
        setFlowTasks([]);
        await load();
    };

    const handleSaved = () => {
        // Refresh the shared alerts store so the sidebar badge and the
        // /today critical banner reflect the new state immediately —
        // no waiting for the 60s poll or a window-focus event.
        refreshAlerts();

        // Reflect the save in the landing counters immediately (optimistic).
        setTasks((prev) =>
            prev.map((t) => {
                const active = flowTasks[flowIndex];
                if (!active) return t;
                if (
                    t.task.id === active.task.id &&
                    t.individual_id === active.individual_id
                ) {
                    return { ...t, measured_today: true };
                }
                return t;
            }),
        );
        // Also flag it inside the flow snapshot so, if the user navigates back
        // via Previous, the ScoringForm knows to prefill (editing existing).
        setFlowTasks((prev) =>
            prev.map((t, idx) =>
                idx === flowIndex ? { ...t, measured_today: true } : t,
            ),
        );
        if (flowIndex + 1 < flowTasks.length) {
            setFlowIndex((i) => i + 1);
        } else {
            goBackToToday();
        }
    };

    if (loading || deepLinkPending) {
        return (
            <div
                className="flex flex-col items-center justify-center p-16 text-slate-500"
                data-testid={
                    deepLinkPending ? "deep-link-loading" : "today-loading"
                }
            >
                <Loader2 className="mb-2 h-6 w-6 animate-spin text-brand-blue-700" />
                <p className="text-sm font-semibold">
                    {deepLinkPending ? "Preparing your task…" : "Loading…"}
                </p>
            </div>
        );
    }

    // ==================== Flow view ====================
    if (inFlow) {
        const item = flowTasks[flowIndex];
        const headerLabel =
            flowMode === "review"
                ? "Reviewing"
                : flowMode === "backlog"
                    ? "Backlog"
                    : "Task";
        return (
            <div className="mx-auto max-w-2xl">
                <FlowHeader
                    label={headerLabel}
                    index={flowIndex}
                    total={flowTasks.length}
                    tone={flowMode}
                    onJump={(newIdx) => setFlowIndex(newIdx)}
                    onBack={goBackToToday}
                    items={flowTasks}
                    individualsById={individuals}
                />

                <div className="mb-4 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() => setFlowIndex((i) => Math.max(i - 1, 0))}
                        disabled={flowIndex === 0}
                        data-testid="prev-task-button"
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-brand-blue-300 hover:text-brand-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        Previous
                    </button>
                    <p
                        className="text-[11px] text-slate-400"
                        data-testid="flow-position"
                    >
                        {flowMode === "review"
                            ? `Editing today's entries`
                            : flowMode === "backlog"
                                ? `Clearing overdue tasks`
                                : `${stats.done} of ${stats.total} scored today`}
                    </p>
                </div>

                <ScoringForm
                    key={item.task.id}
                    item={item}
                    onSaved={handleSaved}
                    onSkip={() =>
                        setFlowIndex((i) =>
                            i + 1 < flowTasks.length ? i + 1 : -1,
                        )
                    }
                />
            </div>
        );
    }

    // ==================== Landing view ====================
    const individualIds = [...new Set(tasks.map((t) => t.individual_id))];
    const effectiveIndividual =
        selectedIndividual || (individualIds.length === 1 ? individualIds[0] : null);
    const handleReviewTask = (taskId) => {
        const found = tasks.find((t) => t.task.id === taskId);
        if (found) {
            setFlowTasks([found]);
            setFlowMode("review");
            setFlowIndex(0);
        }
        setVoiceResult(null);
    };
    const handleVoicePanelClose = () => {
        setVoiceResult(null);
        refreshAlerts();
        load();
    };
    return (
        <div className="mx-auto max-w-2xl" data-testid="today-page">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                    Today
                </p>
                <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                    Ready to document today&apos;s care?
                </h1>

                {backlogTotal > 0 && (
                    <Link
                        to="/backlog"
                        data-testid="critical-banner"
                        className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-red-300 bg-red-50 p-3.5 text-left transition-colors hover:bg-red-100"
                    >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-600 text-white">
                            <AlertOctagon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-black text-red-900">
                                {backlogTotal} overdue task
                                {backlogTotal !== 1 ? "s" : ""}
                            </p>
                            <p className="text-xs text-red-700">
                                Tap to review your backlog
                            </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-red-600" />
                    </Link>
                )}

                {individualIds.length > 0 && (
                    <div className="mt-5 flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            {individualIds.length > 1 && (
                                <>
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Filter by individual
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setSelectedIndividual(null)}
                                            data-testid="filter-individual-all"
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                                                !selectedIndividual
                                                    ? "bg-brand-blue-700 text-white"
                                                    : "bg-white text-slate-600 border border-slate-200"
                                            }`}
                                        >
                                            All ({tasks.length})
                                        </button>
                                        {individualIds.map((id) => (
                                            <button
                                                key={id}
                                                onClick={() => setSelectedIndividual(id)}
                                                data-testid={`filter-individual-${id}`}
                                                className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                                                    selectedIndividual === id
                                                        ? "bg-brand-blue-700 text-white"
                                                        : "bg-white text-slate-600 border border-slate-200"
                                                }`}
                                            >
                                                {individuals[id]
                                                    ? `${individuals[id].first_name} ${individuals[id].last_name}`
                                                    : id.slice(0, 8)}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="shrink-0">
                            <VoiceReportButton
                                individualId={effectiveIndividual}
                                onResult={setVoiceResult}
                            />
                        </div>
                    </div>
                )}

                <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="rounded-2xl border border-brand-blue-100 bg-brand-blue-50 p-4 text-center">
                        <Clock className="mx-auto h-6 w-6 text-brand-blue-700" />
                        <p
                            className="mt-2 text-2xl font-black text-brand-blue-950"
                            data-testid="stat-total"
                        >
                            {stats.total}
                        </p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-blue-800">
                            Total today
                        </p>
                    </div>
                    <div className="rounded-2xl border border-brand-green-100 bg-brand-green-50 p-4 text-center">
                        <CheckCircle2 className="mx-auto h-6 w-6 text-brand-green-700" />
                        <p
                            className="mt-2 text-2xl font-black text-brand-green-900"
                            data-testid="stat-done"
                        >
                            {stats.done}
                        </p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-green-800">
                            Completed
                        </p>
                    </div>
                    <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-center">
                        <AlertCircle className="mx-auto h-6 w-6 text-amber-600" />
                        <p
                            className="mt-2 text-2xl font-black text-amber-900"
                            data-testid="stat-pending"
                        >
                            {stats.pending}
                        </p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                            Pending
                        </p>
                    </div>
                </div>

                <button
                    onClick={startPendingFlow}
                    disabled={stats.pending === 0}
                    data-testid="start-now-button"
                    className="mt-8 flex h-16 w-full items-center justify-center gap-2 rounded-2xl bg-brand-blue-700 text-lg font-black uppercase tracking-wider text-white shadow-lg hover:bg-brand-blue-800 disabled:opacity-60"
                >
                    <PlayCircle className="h-6 w-6" />
                    {stats.pending === 0 && stats.total > 0
                        ? "All done today"
                        : "Start Now"}
                </button>

                {stats.done > 0 && (
                    <button
                        onClick={startReviewFlow}
                        data-testid="review-completed-link"
                        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-amber-700"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                        Review completed entries ({stats.done})
                    </button>
                )}

                {stats.total === 0 && (
                    <p className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">
                        No active plans with tasks yet. Ask a supervisor to
                        upload an ISP.
                    </p>
                )}
            </motion.div>

            {voiceResult && (
                <VoiceResultPanel
                    result={voiceResult}
                    onClose={handleVoicePanelClose}
                    onReviewTask={handleReviewTask}
                />
            )}
        </div>
    );
};

export default TodayPage;
